"use client";

import { useState } from "react";
import { Boxes, FileCode2, Folder, GitBranch, Terminal, ChevronRight } from "lucide-react";

const views = [
  { id: "workspaces", label: "Your workspaces", icon: Boxes },
  { id: "terminal", label: "Run your agents", icon: Terminal },
  { id: "review", label: "Review changes", icon: GitBranch },
] as const;
type View = (typeof views)[number]["id"];

const workspaces = [
  {
    name: "web-app",
    status: "Running",
    agent: "Claude Code",
    specs: [
      ["image", "devgrail/workspace"],
      ["cpu / mem", "2 vCPU · 4 GB"],
      ["volume", "web-app → /workspace"],
      ["url", "app.web-app.example.dev"],
    ],
  },
  {
    name: "api-service",
    status: "Running",
    agent: "Codex",
    specs: [
      ["image", "devgrail/workspace"],
      ["cpu / mem", "1 vCPU · 2 GB"],
      ["volume", "api-service → /workspace"],
      ["branch", "feature/search"],
    ],
  },
];

export function ProductPreview() {
  const [view, setView] = useState<View>("workspaces");
  return (
    <figure aria-label="Illustrated DevGrail workflow" className="mx-auto max-w-5xl">
      <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-[0_24px_80px_-30px_rgba(33,31,29,0.28)]">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b px-5 py-4 sm:px-7">
          <span className="flex items-center gap-2 text-sm font-semibold text-foreground"><Boxes aria-hidden className="size-4 text-foreground" /> DevGrail <ChevronRight aria-hidden className="size-3 text-muted-foreground" /> My projects</span>
          <span className="flex items-center gap-2 font-mono text-xs text-muted-foreground"><span aria-hidden className="size-1.5 rounded-full bg-green-500" /> Your VPS</span>
        </div>
        <div className="grid md:grid-cols-[180px_1fr]">
          <div className="flex flex-wrap gap-1 border-b bg-muted/40 p-3 md:flex-col md:border-b-0 md:border-r md:p-4" role="group" aria-label="Choose a workflow illustration">
            {views.map(({ id, label, icon: Icon }) => (
              <button key={id} type="button" aria-pressed={view === id} aria-controls="workflow-preview" onClick={() => setView(id)} className={`flex items-center gap-2 rounded-lg px-3 py-3 text-left text-xs font-medium transition-colors ${view === id ? "bg-brand-subtle text-brand-ink" : "text-muted-foreground hover:bg-muted hover:text-brand-ink"}`}>
                <Icon aria-hidden className="size-4 shrink-0" />{label}
              </button>
            ))}
            <p className="mt-auto hidden px-3 pt-12 text-xs leading-relaxed text-muted-foreground md:block">One dashboard.<br />Every environment on your own server.</p>
          </div>
          <div id="workflow-preview" className="min-h-[330px] min-w-0 p-5 sm:p-7" aria-live="polite" aria-atomic="true">
            {view === "workspaces" && <>
              <div className="mb-6"><p className="font-mono text-xs text-muted-foreground">PROJECT / NORTHSTAR</p><h2 className="mt-1 text-xl font-semibold">Two workspaces. Two agents. One box.</h2></div>
              <div className="grid gap-3 sm:grid-cols-2">
                {workspaces.map((workspace) => (
                  <div key={workspace.name} className="min-w-0 rounded-xl border bg-background/50 p-4">
                    <div className="flex items-center justify-between gap-2">
                      <span className="flex min-w-0 items-center gap-2"><Folder aria-hidden className="size-4 shrink-0 text-foreground" /><h3 className="truncate text-base font-semibold">{workspace.name}</h3></span>
                      <span className="shrink-0 rounded-full bg-green-50 px-2 py-1 text-[11px] font-medium text-green-800 dark:bg-green-400/15 dark:text-green-300">{workspace.status}</span>
                    </div>
                    <p className="mt-1 text-sm text-muted-foreground">{workspace.agent}</p>
                    <dl className="mt-4 space-y-1.5 font-mono text-[11px] leading-5">
                      {workspace.specs.map(([label, value]) => (
                        <div key={label} className="flex gap-2">
                          <dt className="w-16 shrink-0 text-muted-foreground">{label}</dt>
                          <dd className="min-w-0 break-all text-foreground">{value}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                ))}
              </div>
            </>}
            {view === "terminal" && <>
              <p className="mb-4 flex items-center gap-2 text-sm font-medium"><Terminal aria-hidden className="size-4" /> web-app / terminal session</p>
              <div className="rounded-xl bg-terminal p-5 font-mono text-xs leading-7 text-terminal-foreground ring-1 ring-terminal-border sm:text-sm">
                <p className="text-terminal-muted-foreground">dev@web-app:/workspace$</p>
                <p><span className="text-terminal-muted-foreground">$</span> claude</p>
                <p className="mt-4 text-terminal-foreground">Build a search page for this project.</p>
                <p className="mt-4 text-terminal-muted-foreground">Explore the code. Make changes. Run checks.</p>
                <p className="mt-4 text-brand">Root in its own container. Nothing outside it.</p>
              </div>
              <p className="mt-4 text-xs text-muted-foreground">Sessions keep running while the workspace does — close the tab, reconnect from anywhere.</p>
            </>}
            {view === "review" && <>
              <p className="mb-4 flex items-center gap-2 text-sm font-medium"><GitBranch aria-hidden className="size-4" /> web-app / review changes</p>
              <div className="overflow-hidden rounded-xl border">
                <p className="flex items-center gap-2 border-b bg-muted/50 px-4 py-3 font-mono text-xs"><FileCode2 aria-hidden className="size-4" /> src/search.ts</p>
                <div className="overflow-x-auto py-4 font-mono text-xs leading-8 sm:text-sm">
                  <p className="whitespace-nowrap bg-red-50 px-4 text-red-800 dark:bg-red-400/15 dark:text-red-300">− return allProjects;</p>
                  <p className="whitespace-nowrap bg-green-50 px-4 text-green-800 dark:bg-green-400/15 dark:text-green-300">+ return allProjects.filter(matchesQuery);</p>
                </div>
              </div>
              <h2 className="mt-6 text-lg font-semibold">See what changed before you ship.</h2>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">Browse files, inspect diffs, stage changes, and commit from the built-in Git client.</p>
            </>}
          </div>
        </div>
      </div>
      <figcaption className="mt-4 text-center text-xs text-muted-foreground">Workflow illustration with example projects. Explore the three views above.</figcaption>
    </figure>
  );
}
